import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import api from '../../../shared/api';
import config from '../../../config';
import { AddIcon, DeleteIcon } from '../../../shared/components/Icons';
import LoadingAnimation from '../../../shared/components/LoadingAnimation';
import EditableTextbox from '../../../shared/components/editable/EditableTextbox';
import YesNoModal from '../../../shared/components/YesNoModal';
import OkCancelModal from '../../../shared/components/OkCancelModal';

import { injectStateService, StateService } from '../../../shared/services/StateService';
import { CanCreate, CanDelete } from '../../../shared/components/security';
import authService, { privileges } from '../../../shared/services/AuthService';
import messages from './tagSettingsMessages';

class RelationTagOverview extends Component {
  state = {
    tags: null,
    newTag: null,
    tagToDelete: null,
  };

  componentDidMount() {
    api.base.get(`${config.appUrls.api}/api/relationtag/overview`).then(({ data: tags }) => {
      this.setState({
        tags,
      });
    });
  }

  addTag = () => {
    const { newTag, tags } = this.state;
    const { intl } = this.props;

    if (!newTag) return;

    if (tags.find(x => x.name.toLowerCase() === newTag.toLowerCase())) {
      ToastrService.error(intl.formatMessage(messages.TagAlreadyExists));
      return;
    }

    api.base.post(`${config.appUrls.api}/api/relationtag`, { name: newTag }).then(({ data }) => {
      data.nrOfRelationsInUse = 0;
      if (tags.find(x => x.id === data.id)) {
        // Tag already exists
        this.setState({
          newTag: null,
        });
      } else {
        this.setState(state => ({
          tags: [...state.tags, data],
          newTag: null,
        }));
      }
    });
  };

  editTagName = (tag, value) =>
    api.base
      .put(`${config.appUrls.api}/api/relationtag`, { ...tag, name: value })
      .then(
      ({ data }) => {
        const { name } = data;
        this.setState(state => ({
          tags: state.tags.map(t => {
            if (t.id === tag.id) {
              return { ...t, name };
            }
            return t;
          }),
        }));
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
      },
    );

  deleteTag = (tag, force = false) => {
    if (tag.documentCount === 0 || force) {
      return api.base.delete(`${config.appUrls.api}/api/relationtag/${tag.id}`).then(() => {
        this.setState(state => ({
          tags: state.tags.filter(t => t.id !== tag.id),
          tagToDelete: null,
        }));
      });
    }
    this.setState({ tagToDelete: tag });
  };

  render() {
    const { tags, newTag, tagToDelete } = this.state;
    const { intl, stateService } = this.props;

    const canUpdate = authService.hasPermission({
      functionality: 'Tags',
      privilege: privileges.Update,
    });

    return (
      <>
        {tags ? (
          <>
            <div className="panel-heading">
              <CanCreate on="Tags">
                <div className="panel-buttons">
                  <button
                    type="button"
                    className="btn btn-inverse"
                    onClick={() => this.setState({ newTag: '' })}
                  >
                    <i className="fa fa-plus" /> <FormattedMessage {...messages.AddTag} />
                  </button>
                </div>
              </CanCreate>
            </div>
            <div className="panel-body">
              <table className="table table-grid document-tag-grid">
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage {...messages.Name} />
                    </th>
                    <th className="text-right">
                      <FormattedMessage {...messages.NrOfRelations} />
                    </th>
                    <th className="text-right">
                      <FormattedMessage {...messages.Actions} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tags.map(tag => (
                    <tr key={tag.id}>
                      <td>
                        <EditableTextbox
                          value={tag.name}
                          onChange={value => this.editTagName(tag, value)}
                          maxLength="200"
                          readOnly={!canUpdate}
                        />
                      </td>

                      <td className="text-right">
                        {tag.nrOfRelationsInUse === 0 ? (
                          0
                        ) : (
                          <a
                            style={{ display: 'block' }}
                            className="bold"
                            href={stateService.getStateUrl('relations.overview', {
                              json: { tags: [tag.name] },
                            })}
                            onClick={() => stateService.gotoRelationOverview({ tags: [tag.name] })}
                          >
                            {tag.nrOfRelationsInUse}
                          </a>
                        )}
                      </td>

                      <td className="text-right">
                        <CanDelete on="Tags">
                          <a onClick={() => this.deleteTag(tag)} className="btn btn-xs btn-icon">
                            <DeleteIcon />
                          </a>
                        </CanDelete>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <OkCancelModal
                show={newTag !== null}
                title={intl.formatMessage(messages.AddTag)}
                onClose={() => this.setState({ newTag: null })}
                onSave={() => this.addTag()}
              >
                {newTag !== null && (
                  <input
                    type="text"
                    className="form-control"
                    placeholder={intl.formatMessage(messages.EnterTag)}
                    required
                    value={newTag}
                    onChange={e => this.setState({ newTag: e.target.value })}
                    onKeyDown={e => {
                      if (e.keyCode === 13) {
                        this.addTag();
                      } else if (e.keyCode === 27) {
                        this.setState({ newTag: null });
                      }
                    }}
                    autoFocus
                  />
                )}
              </OkCancelModal>

              <YesNoModal
                show={!!tagToDelete}
                title={intl.formatMessage(messages.DeleteTag)}
                message={intl.formatMessage(messages.DeleteRelationTagConfirmationMsg)}
                onNo={() => this.setState({ tagToDelete: null })}
                onSave={() => this.deleteTag(tagToDelete, true)}
              />
            </div>
          </>
        ) : (
          <LoadingAnimation />
        )}
      </>
    );
  }
}

RelationTagOverview.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(RelationTagOverview));
